import { useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Lock, Mail, AlertCircle, Eye, EyeOff } from 'lucide-react';
import logoImage from '@/assets/logo.png';

export function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login({ email, password });
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ||
        'Email ou senha inválidos';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-bg-main p-4 sm:p-6">
      {/* Atmosfera de marca — glow azul discreto atrás do card (sem gradient estrangeiro) */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-[-12%] size-[440px] -translate-x-1/2 rounded-full bg-primary/10 blur-[120px]" />
      </div>

      <div className="relative w-full max-w-md animate-fade-up">
        {/* Auth card */}
        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-elevated">
          {/* Fio de topo: azul → pistache (eco das duas cores do logo) */}
          <div className="h-1 w-full bg-gradient-to-r from-primary to-tertiary" />

          <div className="px-6 py-8 sm:px-8 sm:py-10">
            {/* Logo + cabeçalho */}
            <div className="mb-8 text-center">
              <img
                src={logoImage}
                alt="Curral Tech"
                className="mx-auto mb-5 h-24 w-auto object-contain sm:h-28"
              />
              <h1 className="text-2xl font-bold tracking-tight text-text-title sm:text-[28px]">
                Painel Administrativo
              </h1>
              <p className="mt-1.5 text-sm text-text-secondary">
                Entre com suas credenciais para acessar
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5" aria-label="Formulário de login">
              {error && (
                <div
                  role="alert"
                  className="flex items-start gap-3 rounded-lg border border-error/20 bg-error/10 p-3 text-sm text-error"
                >
                  <AlertCircle size={18} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div>
                <label htmlFor="email" className="mb-2 block text-sm font-medium text-text-title">
                  Email
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <Mail size={20} className="text-text-hint" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="block w-full rounded-lg border border-border-strong bg-bg-elevated py-3 pl-11 pr-4 text-text-title transition-colors placeholder:text-text-hint hover:border-primary/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="seu@email.com"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="mb-2 block text-sm font-medium text-text-title"
                >
                  Senha
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <Lock size={20} className="text-text-hint" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="block w-full rounded-lg border border-border-strong bg-bg-elevated py-3 pl-11 pr-11 text-text-title transition-colors placeholder:text-text-hint hover:border-primary/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    aria-pressed={showPassword}
                    className="absolute inset-y-0 right-0 m-1 flex items-center rounded-md px-2 text-text-hint transition-colors hover:text-text-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  id="remember"
                  type="checkbox"
                  className="size-4 cursor-pointer rounded border-border-strong accent-primary"
                />
                <label
                  htmlFor="remember"
                  className="ml-2 cursor-pointer text-sm text-text-secondary"
                >
                  Lembrar de mim
                </label>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-cta text-base font-semibold text-white shadow-cta transition-all hover:bg-cta-hover active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-cta/60 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-card disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isLoading ? (
                  <>
                    <div className="size-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                    <span>Entrando…</span>
                  </>
                ) : (
                  'Entrar'
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-text-secondary">
            © {year} Curral Tech. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </div>
  );
}
